import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, Globe, Bot, Wand2 } from "lucide-react";

const MODELS = [
  { id: "claude",  label: "Claude Sonnet 4.6",       tag: "Recommended", tagline: "Nuanced strategy, long-form copy", Icon: Wand2 },
  { id: "chatgpt", label: "ChatGPT · GPT 5.6 Terra", tag: "New",         tagline: "Bold, structured, action-first",   Icon: Bot },
];

export default function UrlStep({ url, setUrl, model, setModel, busy, onAnalyze }) {
  return (
    <div className="fade-up">
      <span className="chip"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> One field. That's it.</span>
      <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight text-zinc-900 mt-6 leading-[1.05]">
        Paste your website.<br /> We handle the rest.
      </h1>
      <p className="text-zinc-600 mt-5 max-w-xl text-lg">
        We'll read your site, infer your audience, USP and competitors — then show you a one-page brief you can nudge before we build the plan.
      </p>

      <div className="mt-10 bg-white border border-zinc-200 rounded-2xl p-6 md:p-8">
        <Label className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Website URL</Label>
        <div className="mt-2 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Globe className="h-5 w-5 text-zinc-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              data-testid="input-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && onAnalyze()}
              placeholder="brightsmile.example.com"
              className="h-14 pl-11 text-base"
              autoFocus
            />
          </div>
          <Button
            onClick={onAnalyze}
            disabled={busy}
            data-testid="btn-analyze"
            className="btn-lift h-14 px-8 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
          >
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reading…</> : <><Sparkles className="h-4 w-4 mr-2" />Analyze</>}
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mt-3">
          We only read the public homepage. You'll be able to edit the brief before generating.
        </p>

        <div className="mt-8 border-t border-zinc-100 pt-6">
          <Label className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Which AI should build your plan?</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3" data-testid="model-picker">
            {MODELS.map((m) => {
              const active = model === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`model-${m.id}`}
                  onClick={() => setModel(m.id)}
                  className={`text-left border rounded-xl p-4 transition-colors ${
                    active ? "border-indigo-600 bg-indigo-50/40 ring-2 ring-indigo-100" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${active ? "bg-indigo-600 text-white" : "bg-zinc-100 text-zinc-700"}`}>
                      <m.Icon className="h-4 w-4" />
                    </div>
                    <div className="text-sm font-medium">{m.label}</div>
                    <span className={`ml-auto text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${m.tag === "Recommended" ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}>
                      {m.tag}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">{m.tagline}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
